import soundMessageUrl from "../../assets/new_message.mp3";
import soundTicketUrl  from "../../assets/new_chat_crop.mp3";
import {
	Badge,
	IconButton,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Popover,
	Tooltip
} from "@mui/material";
import { styled } from "@mui/material/styles";
import ChatIcon from "@mui/icons-material/Chat";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import MessageIcon from "@mui/icons-material/Message";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { format } from "date-fns";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { getImageUrl } from "../../helpers/imageHelper";
import useTickets from "../../hooks/useTickets";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import TicketListItem from "../TicketListItem";

const TabContainer = styled('div')(({ theme }) => ({
	overflowY: "auto",
	maxHeight: 350,
	...(theme.scrollbarStyles || {}),
}));

const PopoverPaper = styled(Popover)(({ theme }) => ({
	'& .MuiPopover-paper': {
		width: "100%",
		maxWidth: 350,
		marginLeft: theme.spacing(2),
		marginRight: theme.spacing(1),
		[theme.breakpoints.down("sm")]: {
			maxWidth: 270,
		},
	}
}));

const urlBase64ToUint8Array = (base64String) => {
	const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
	const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
	const rawData = window.atob(base64);
	return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
};

const subscribeToPush = async () => {
	if (!("PushManager" in window) || !("serviceWorker" in navigator)) return;
	try {
		const sw = await navigator.serviceWorker.ready;
		const existing = await sw.pushManager.getSubscription();
		if (existing) return;
		const { data } = await api.get("/push-subscriptions/vapid-public-key");
		if (!data.publicKey) return;
		const subscription = await sw.pushManager.subscribe({
			userVisibleOnly: true,
			applicationServerKey: urlBase64ToUint8Array(data.publicKey)
		});
		await api.post("/push-subscriptions", { subscription });
	} catch (err) {
		console.error("[PUSH] Erro ao subscrever:", err);
	}
};

// Singleton AudioContext + buffers pré-decodificados no primeiro gesto
let sharedAc = null;
let bufferMessage = null;
let bufferTicket = null;

const getAc = () => {
	if (!sharedAc) {
		sharedAc = new (window.AudioContext || window.webkitAudioContext)();
	}
	return sharedAc;
};

const loadBuffer = async (url) => {
	const res = await fetch(url);
	const ab = await res.arrayBuffer();
	return getAc().decodeAudioData(ab);
};

const preloadSounds = () => {
	Promise.all([loadBuffer(soundMessageUrl), loadBuffer(soundTicketUrl)])
		.then(([m, t]) => { bufferMessage = m; bufferTicket = t; })
		.catch(() => {});
};

const unlockAudio = () => {
	getAc().resume().then(preloadSounds).catch(() => {});
	document.removeEventListener('click',      unlockAudio, true);
	document.removeEventListener('keydown',    unlockAudio, true);
	document.removeEventListener('touchstart', unlockAudio, true);
};
document.addEventListener('click',      unlockAudio, true);
document.addEventListener('keydown',    unlockAudio, true);
document.addEventListener('touchstart', unlockAudio, true);

const playBuffer = (buffer) => {
	if (!buffer) return;
	const c = getAc();
	c.resume().then(() => {
		const src = c.createBufferSource();
		src.buffer = buffer;
		src.connect(c.destination);
		src.start(0);
	}).catch(() => {});
};

const playMessageSound = () => playBuffer(bufferMessage);
const playTicketSound  = () => playBuffer(bufferTicket);

// Intervalo entre checagens de canais sem atendente atribuído (issue #112).
const CHANNEL_ORPHAN_CHECK_INTERVAL_MS = 5 * 60 * 1000;

const NotificationsPopOver = () => {
	const themeStorage = localStorage.getItem("theme");
	const { t } = useTranslation();
	const navigate = useNavigate();
	const location = useLocation();
	const { user } = useContext(AuthContext);
	const ticketIdUrl = +location.pathname.split("/")[2];
	const anchorEl = useRef();
	const [isOpen, setIsOpen] = useState(false);
	const [notifications, setNotifications] = useState([]);
	const [isAudioEnabled, setIsAudioEnabled] = useState(
		localStorage.getItem("userAudioEnabled") !== "false"
	);
	const [notificationsAllowed, setNotificationsAllowed] = useState(
		("Notification" in window) && Notification.permission === "granted"
	);
	const { tickets } = useTickets({ withUnreadMessages: "true", all: true });
	const [channelWarnings, setChannelWarnings] = useState([]);
	const notifiedChannelIdsRef = useRef(new Set());
	const [baseTitle, setBaseTitle] = useState("AD Ticket");

	// Checagem periódica de canais sem nenhum atendente atribuído (issue #112).
	// Só faz sentido para quem pode corrigir a atribuição (admin/masteradmin).
	useEffect(() => {
		const isAdminUser = user?.profile === "admin" || user?.profile === "masteradmin";
		if (!isAdminUser) return undefined;

		let cancelled = false;

		const checkOrphanChannels = async () => {
			try {
				const { data } = await api.get("/whatsapp/");
				if (cancelled) return;

				const orphans = (data || []).filter(whatsApp => (whatsApp.assignedUsersCount ?? 0) === 0);
				setChannelWarnings(orphans);

				orphans.forEach(whatsApp => {
					if (!notifiedChannelIdsRef.current.has(whatsApp.id)) {
						notifiedChannelIdsRef.current.add(whatsApp.id);
						toast.warning(
							t("notifications.channelWithoutAttendantToast", { name: whatsApp.name }),
							{ toastId: `channel-orphan-${whatsApp.id}` }
						);
					}
				});

				// Libera o canal para notificar de novo caso volte a ficar órfão futuramente
				notifiedChannelIdsRef.current.forEach(id => {
					if (!orphans.some(whatsApp => whatsApp.id === id)) {
						notifiedChannelIdsRef.current.delete(id);
					}
				});
			} catch (err) {
				// Falha silenciosa: checagem em segundo plano, não deve incomodar o usuário
			}
		};

		checkOrphanChannels();
		const interval = setInterval(checkOrphanChannels, CHANNEL_ORPHAN_CHECK_INTERVAL_MS);

		return () => {
			cancelled = true;
			clearInterval(interval);
		};
	}, [user, t]);

	// Re-verifica permissão quando o usuário volta para a aba ou foca a janela
	useEffect(() => {
		const checkPermission = () => {
			if ("Notification" in window) {
				setNotificationsAllowed(Notification.permission === "granted");
			}
		};
		window.addEventListener("focus", checkPermission);
		document.addEventListener("visibilitychange", checkPermission);
		return () => {
			window.removeEventListener("focus", checkPermission);
			document.removeEventListener("visibilitychange", checkPermission);
		};
	}, []);

	useEffect(() => {
		setNotifications((tickets || []).filter(t => t.status !== "closed"));
	}, [tickets]);

	// Se permissão já foi concedida anteriormente, garantir a subscription push
	useEffect(() => {
		if (("Notification" in window) && Notification.permission === "granted") {
			subscribeToPush();
		}
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	const requestNotificationPermission = () => {
		if (!("Notification" in window)) {
			alert(t("notifications.unsupported"));
			return;
		}

		Notification.requestPermission()
			.then((permission) => {
				if (permission === "granted") {
					setNotificationsAllowed(true);
					toast.success(t("notifications.permissionGranted"), { toastId: "notif-permission-granted" });
					subscribeToPush();
				} else if (permission === "denied") {
					toast.error(t("notifications.permissionDenied"), { toastId: "notif-permission-denied" });
				}
			})
			.catch((error) => {
				console.error("Erro ao solicitar permissão para notificações:", error);
				toast.error(t("notifications.error"), { toastId: "notif-permission-error" });
			});
	};

	// Toast proeminente na primeira visita quando permissão ainda não foi solicitada
	useEffect(() => {
		if (sessionStorage.getItem("notifPermissionToastShown")) return;
		sessionStorage.setItem("notifPermissionToastShown", "1");

		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
		const isStandalone = window.navigator.standalone === true;

		// iOS fora do PWA instalado: notificações só funcionam via home screen
		if (isIOS && !isStandalone) {
			toast.info(
				"Para receber notificações no iPhone/iPad, instale o app: toque em Compartilhar → Adicionar à Tela de Início.",
				{ autoClose: 20000, toastId: "notif-permission-request" }
			);
			return;
		}

		if (!("Notification" in window) || Notification.permission !== "default") return;

		const askPermission = () => {
			Notification.requestPermission().then((perm) => {
				if (perm === "granted") {
					setNotificationsAllowed(true);
					subscribeToPush();
				}
			}).catch(() => {});
		};

		toast.info(
			<span>
				Ative as notificações do navegador para receber alertas de novos tickets.{" "}
				<button
					onClick={askPermission}
					style={{ background: "none", border: "none", color: "inherit", textDecoration: "underline", cursor: "pointer", padding: 0, font: "inherit" }}
				>
					Ativar agora
				</button>
			</span>,
			{ autoClose: 15000, toastId: "notif-permission-request" }
		);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	// Ref com os valores mais recentes — evita recriar o socket a cada
	// navegação ou toggle de áudio.
	const appMessageHandlerRef = useRef(null);

	useEffect(() => {
		appMessageHandlerRef.current = (data) => {
			if (!data.ticket) return;

			const UserQueues = user?.queues?.findIndex(
				(users) => users.id === data.ticket.queueId
			);
			const isAdminUser = user?.profile === "admin" || user?.profile === "masteradmin";
			const isUserQueue = UserQueues !== -1 || !data.ticket.queueId;

			const shouldNotifyOpenTicket = data.ticket.status === "open" &&
				(data.ticket.userId === user?.id || isAdminUser);
			const shouldNotifyPendingTicket = data.ticket.status === "pending" &&
				(isUserQueue || isAdminUser);

			if (
				data.action === "create" &&
				!data.message.read &&
				(shouldNotifyOpenTicket || shouldNotifyPendingTicket)
			) {
				if (data.ticket.status === "closed") {
					setNotifications((prevState) => prevState.filter((t) => t.id !== data.ticket.id));
					return;
				}
				setNotifications((prevState) => {
					const ticketIndex = prevState.findIndex((t) => t.id === data.ticket.id);
					if (ticketIndex !== -1) {
						prevState[ticketIndex] = data.ticket;
						return [...prevState.filter(t => t.status !== "closed")];
					}
					return [data.ticket, ...prevState].filter(t => t.status !== "closed");
				});

				// URL lida no momento do evento, não de closure estático
				const currentTicketIdUrl = +window.location.pathname.split("/")[2];
				const shouldNotNotify = data.message.ticketId === currentTicketIdUrl &&
					document.visibilityState === "visible";

				if (shouldNotNotify) return;

				if (notificationsAllowed) {
					const { contact, ticket } = data;
					const msgPreview = data.message?.body
						? (data.message.body.length > 80
							? `${data.message.body.substring(0, 80)}…`
							: data.message.body)
						: format(new Date(), "HH:mm");
					const options = {
						body: msgPreview,
						icon: "/logo192.png",
						tag: String(ticket.id),
						renotify: true,
						data: { ticketId: ticket.id },
					};
					const title = `${t("tickets.notification.message")} ${contact.name}`;
					try {
						if ("serviceWorker" in navigator) {
							navigator.serviceWorker.ready
								.then(reg => reg.showNotification(title, options))
								.catch(() => new Notification(title, options));
						} else {
							new Notification(title, options);
						}
					} catch (error) {
						console.error("[NOTIFICAÇÃO] Erro ao exibir notificação:", error);
					}
				}

				if (isAudioEnabled) {
					if (shouldNotifyPendingTicket) {
						playTicketSound();
					} else {
						playMessageSound();
					}
				}
			}
		};
	}, [user, notificationsAllowed, isAudioEnabled, t]);

	useEffect(() => {
		const socket = openSocket();
		if (!socket) return;

		const handler = (data) => appMessageHandlerRef.current?.(data);

		socket.on("appMessage", handler);

		return () => {
			socket.off("appMessage", handler);
		};
	}, []);

	// Busca a personalização (título/favicon) apenas quando o tema muda,
	// não a cada notificação nova — evita repetir a chamada e re-tocar o favicon sem necessidade.
	useEffect(() => {
		const updatePersonalization = async () => {
			try {
				const { data } = await api.get("/personalizations");
				let title = "AD Ticket";
				let faviconUrl = null;
				if (data && data.length > 0) {
					const lightConfig = data.find((config) => config.theme === "light");
					const darkConfig = data.find((config) => config.theme === "dark");

					if (themeStorage === "light" && lightConfig) {
						faviconUrl = lightConfig.favico || null;
						title = lightConfig.company || title;
					} else if (themeStorage === "dark" && darkConfig) {
						faviconUrl = darkConfig.favico || null;
					}
				}
				setBaseTitle(title);

				const resolvedHref = faviconUrl
					? (faviconUrl.startsWith('/') || faviconUrl.startsWith('http')
						? faviconUrl
						: `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/public/logos/${faviconUrl}`)
					: `${process.env.PUBLIC_URL}/favicon.ico`;

				// Nunca remove o <link rel="icon">: removê-lo faz o navegador buscar
				// /favicon.ico na raiz por conta própria, gerando o flash de troca de ícone.
				// Em vez disso, sempre ajustamos o href — para o customizado ou de volta ao padrão.
				const existingLink = document.querySelector("link[rel~='icon']");
				if (existingLink) {
					if (!existingLink.href.endsWith(resolvedHref)) {
						existingLink.href = resolvedHref;
					}
				} else {
					const link = document.createElement("link");
					link.rel = "icon";
					link.href = resolvedHref;
					document.getElementsByTagName("head")[0].appendChild(link);
				}
			} catch (err) {
				toastError(err, t);
			}
		};

		updatePersonalization();
	}, [themeStorage, t]);

	// Título muda com a contagem de notificações, sem precisar rebuscar a personalização.
	useEffect(() => {
		document.title =
			notifications.length > 0
				? `(${notifications.length}) ${baseTitle}`
				: baseTitle;
	}, [notifications, baseTitle]);

	const toggleAudio = () => {
		const newAudioStatus = !isAudioEnabled;
		setIsAudioEnabled(newAudioStatus);
		localStorage.setItem("userAudioEnabled", newAudioStatus.toString());
	};

	const handleClick = () => {
		setIsOpen((prevState) => !prevState);
	};

	const handleClickAway = () => {
		setIsOpen(false);
	};

	const NotificationTicket = ({ children }) => (
		<div onClick={handleClickAway}>{children}</div>
	);

	return (
		<>
			<IconButton onClick={toggleAudio} aria-label="Toggle Sound" color="inherit">
				{isAudioEnabled ? <VolumeUpIcon /> : <VolumeOffIcon />}
			</IconButton>
			{isAudioEnabled && (
				<>
					<Tooltip title="Testar: nova mensagem" arrow>
						<IconButton
							onClick={playMessageSound}
							aria-label="Test Message Sound"
							color="inherit"
							size="small"
						>
							<MessageIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Testar: novo ticket na fila" arrow>
						<IconButton
							onClick={playTicketSound}
							aria-label="Test Ticket Sound"
							color="inherit"
							size="small"
						>
							<ConfirmationNumberIcon fontSize="small" />
						</IconButton>
					</Tooltip>
				</>
			)}
			{!notificationsAllowed && (
				<IconButton
					onClick={requestNotificationPermission}
					color="inherit"
					variant="contained"
				>
					<Tooltip title={t("notifications.allow")} arrow>
						<HelpOutlineIcon />
					</Tooltip>
				</IconButton>
			)}
			<IconButton
				onClick={handleClick}
				ref={anchorEl}
				aria-label="Open Notifications"
				color="inherit"
			>
				<Badge
					badgeContent={notifications.length + channelWarnings.length}
					color="secondary"
					overlap="rectangular"
					max={999999}
				>
					<ChatIcon />
				</Badge>
			</IconButton>
			<PopoverPaper
				disableScrollLock
				open={isOpen}
				anchorEl={anchorEl.current}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				onClose={handleClickAway}
			>
				<List dense component={TabContainer}>
					{channelWarnings.map((whatsApp) => (
						<ListItem
							key={`channel-warning-${whatsApp.id}`}
							button
							onClick={() => {
								handleClickAway();
								navigate("/channels");
							}}
						>
							<ListItemIcon>
								<WarningAmberIcon color="warning" />
							</ListItemIcon>
							<ListItemText
								primary={whatsApp.name}
								secondary={t("notifications.channelWithoutAttendant", { name: whatsApp.name })}
							/>
						</ListItem>
					))}
					{notifications.length === 0 && channelWarnings.length === 0 ? (
						<ListItem>
							<ListItemText>{t("notifications.noTickets")}</ListItemText>
						</ListItem>
					) : (
						notifications.map((ticket) => (
							<NotificationTicket key={ticket.id}>
								<TicketListItem ticket={ticket} />
							</NotificationTicket>
						))
					)}
				</List>
			</PopoverPaper>
		</>
	);
};

export default NotificationsPopOver;
